import {useState, useCallback, useEffect, useRef} from 'react';
import {
    CompanyAnnual,
    getAnnual,
    updateAnnual,
    createAnnual,
    type CompanyAnnualPayload,
} from '@/services/accountService/companyAnnualService';
import {getCompany} from '@/services/accountService/companyService';
import {AuditFsAnnualService} from '@/services/auditService';
import {ensureFsCreateTxStarted} from '@/services/auditService/fsCreateTxService';
import {
    type CompanyAnnualCreateFlow,
    validateCompanyAnnualFormCreateFlow,
} from '@/utils/companyAnnualFlowGuard';
import {useAutoSave} from '@/pages/components/audit/finance-statement/create-stepper/hooks/useAutoSave';

interface UseCompanyAnnualFormOptions {
    annualId?: number;
    companyId?: number;
    autoSave?: boolean;
    initialData?: CompanyAnnual | null;
    onLoadComplete?: (data: CompanyAnnual) => void;
    onSaveComplete?: (data: CompanyAnnual) => void;
    onCreateBlocked?: (message: string) => void;
    createGuardFlow?: CompanyAnnualCreateFlow;
    createFlowType?: 1 | 2;
}

export function useCompanyAnnualForm({
                                         annualId,
                                         companyId,
                                         autoSave = true,
                                         initialData,
                                         onLoadComplete,
                                         onSaveComplete,
                                         onCreateBlocked,
                                         createGuardFlow,
                                         createFlowType,
                                     }: UseCompanyAnnualFormOptions) {
    const [form, setForm] = useState<CompanyAnnual | null>(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // 使用 ref 保存回调函数，避免依赖问题
    const onLoadCompleteRef = useRef(onLoadComplete);
    const onSaveCompleteRef = useRef(onSaveComplete);
    const onCreateBlockedRef = useRef(onCreateBlocked);

    useEffect(() => {
        onLoadCompleteRef.current = onLoadComplete;
        onSaveCompleteRef.current = onSaveComplete;
        onCreateBlockedRef.current = onCreateBlocked;
    }, [onLoadComplete, onSaveComplete, onCreateBlocked]);

    useEffect(() => {
        // 如果有初始数据，直接使用
        if (initialData) {
            setForm(initialData);
            onLoadCompleteRef.current?.(initialData);
            return;
        }

        if (!annualId && !companyId) {
            return;
        }

        const loadData = async () => {
            setLoading(true);
            setError(null);

            try {
                if (annualId) {
                    const data = await getAnnual(annualId);
                    setForm(data);
                    onLoadCompleteRef.current?.(data);
                } else if (companyId) {
                    // Prefill company-level limitByGuarantee (do not default 0 over company truth)
                    let companyFlag = 0;
                    let companyLocked = false;
                    try {
                        const company = await getCompany(companyId);
                        companyFlag = Number(company?.limitByGuarantee ?? 0) === 1 ? 1 : 0;
                        companyLocked = Boolean(company?.limitByGuaranteeLocked);
                    } catch {
                        // keep defaults when company get fails
                    }
                    const empty: CompanyAnnual = {
                        annualId: 0,
                        companyId: companyId || 0,
                        companyName: '',
                        periodFrom: null,
                        periodTo: null,
                        lastYearPeriodFrom: null,
                        lastYearPeriodTo: null,
                        yearOfAssessment: null,
                        taxFileNo: null,
                        limitByGuarantee: companyFlag,
                        limitByGuaranteeLocked: companyLocked,
                        liabilitiesCurrency: '',
                        liabilitiesAmount: 0,
                        otherNameThanCompany: null,
                        section88: 0,
                        bankConfirmations: [],
                        qualifyOpinion: null as any,
                        firstAudit: null as any,
                        formerAuditor: '',
                        addressOfFormerAuditor: '',
                        professionalClearance: null,
                        auditCertificateId: 0,
                        auditStaffs: [],
                        registeredOffice: null,
                        directors: [],
                        shareholders: [],
                        monthsInPeriod: 0,
                        daysInPeriod: 0,
                        auditMonths: 0,
                        isChangeName: 0,
                        offShoreClaim: 0,
                        changeEffectiveDate: null,
                        oldCompanyName: null,
                        oldCompanyChineseName: null,
                        currentCompanyName: null,
                        currentCompanyChineseName: null,
                        companyChineseName: null,
                        changeOfFinancialYearEnd: 0,
                        dateOfBoardResolution: null,
                        reasonToChange: null,
                        cessationDuringThisPeriod: 0,
                        isMultiYearAudit: true,
                        presentationCurrency: 'HKD',
                        exchangeRate: 1,
                        functionalCurrency: null,
                        dividendPaid: 0,
                        dividendCurrency: null,
                        amountPerEachShare: 0,
                        totalDividendAmount: 0,
                        twoTierTaxThisYear: 1,
                        twoTierTaxLastYear: 1,
                        assessableResultType: 0,
                        assessableAmount: 0,
                        taxLossBF: 0,
                        taxLossCF: 0,
                        changeOfAddress: 0,
                        lastAddress: null,
                        changeDate: null,
                        ppe: 0,
                        relatedPartyTransactions: 0,
                        rentalCommitment: 0,
                        holdingCo: 0,
                        nameOfImmediate: null,
                        immediateHolding: null,
                        nameOfUltimate: null,
                        ultimateHolding: null,
                        allotmentOfShares: 0,
                        allotmentAmount: 0,
                        noOfShares: 0,
                        pricePerShare: 0,
                        priceCurrency: null,
                        dividendPaidOfOverseas: 0,
                        overseasDividendCurrency: null,
                        overseasDividendAmountPerShare: 0,
                        overseasTotalDividendAmount: 0,
                        principalActivities: 0,
                        businessNature: null,
                        commenceWithinPeriod: 0,
                        commenceDate: null,
                        netLiabilities: 0,
                        pettyCashConfirmation: '',
                        pettyCashAmount: 0,
                        irdLetterSigner: '',
                        taxRepSameAsAuditor: 1,
                        taxRepName: null,
                        taxRepAddress: null,
                        taxRepTel: null,
                        taxRepBrNumber: null,
                        referrer: null,
                        inCharge: null,
                    };
                    setForm(empty);
                    onLoadCompleteRef.current?.(empty);
                }
            } catch (e: any) {
                setError(e?.message || 'Failed to load data');
                console.error('Load data error:', e);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [annualId, companyId, initialData]);

    const saveData = useCallback(
        async (data: CompanyAnnual) => {
            if (!data) return;

            setSaving(true);
            try {
                await ensureFsCreateTxStarted();
                const seen = new Set<number>();
                const cleanAuditStaffs = (data.auditStaffs || [])
                    .filter((s) => s && Number(s.userId || 0) > 0)
                    .filter((s) => {
                        const id = Number(s.userId);
                        if (seen.has(id)) return false;
                        seen.add(id);
                        return true;
                    })
                    .map((s) => ({userId: Number(s.userId), roleType: Number(s.roleType || 0)}));

                const payload: CompanyAnnualPayload = {
                    ...data,
                    functionalCurrency: data.functionalCurrency ?? 'HKD',
                    liabilitiesCurrency: data.liabilitiesCurrency ?? 'HKD',
                    auditStaffs: cleanAuditStaffs
                };

                let result: CompanyAnnual;
                if (data.annualId && data.annualId > 0) {
                    result = await updateAnnual(data.annualId, payload);
                } else {
                    if (createGuardFlow) {
                        const blockedMessage = await validateCompanyAnnualFormCreateFlow({
                            form: payload as CompanyAnnual,
                            expectedFlow: createGuardFlow,
                        });
                        if (blockedMessage) {
                            onCreateBlockedRef.current?.(blockedMessage);
                            throw new Error(blockedMessage);
                        }
                    }
                    result = await createAnnual(payload);
                    if (createFlowType && result.annualId && result.companyId) {
                        try {
                            await AuditFsAnnualService.initFsStatus(result.companyId, result.annualId, createFlowType);
                        } catch (_) {
                        }
                    }
                }

                setForm(result);
                onSaveCompleteRef.current?.(result);
                return result;
            } catch (e: any) {
                console.error('Save failed:', e);
                throw e;
            } finally {
                setSaving(false);
            }
        },
        [createFlowType, createGuardFlow]
    );

    const {triggerSave, forceSave, cancelSave} = useAutoSave({
        onSave: saveData,
        enabled: autoSave,
    });

    const updateForm = useCallback(
        (patch: Partial<CompanyAnnual>) => {
            setForm((prev) => {
                if (!prev) return prev;
                const updated = {...prev, ...patch};
                if (autoSave) {
                    triggerSave(updated);
                }
                return updated;
            });
        },
        [autoSave, triggerSave]
    );

    return {
        form,
        loading,
        saving,
        error,
        updateForm,
        forceSave,
        cancelSave,
        setForm,
        saveData,
    };
}
